/**
 * Les mots à taper, et ce qu'ils déclenchent — sans DOM, sans React.
 *
 * Le site n'a aucun sélecteur d'interface à l'écran : on recharge la page pour
 * retomber au hasard, ou on tape un mot. Trois mots existaient déjà, qui font
 * quelque chose. **Onze s'y ajoutent, un par interface, pour qui sait déjà où
 * il veut aller** — le tirage au sort reste le chemin par défaut, ceux-là sont
 * le raccourci.
 *
 * Rien n'est indiqué à l'écran, hormis les indices des pieds de page.
 */

import type { Skin } from './types';

export type SecretAction =
  /** Change d'interface au hasard, mais jamais pour celle qu'on quitte. */
  | { readonly kind: 'reroll' }
  /** Ouvre le bac caché. */
  | { readonly kind: 'egg' }
  /** Colle une découpe en papier, sans rien interrompre. */
  | { readonly kind: 'fish' }
  /** Va droit à une interface nommée. */
  | { readonly kind: 'skin'; readonly skin: Skin };

/**
 * Les onze raccourcis d'interface.
 *
 * Les mots sont ceux qu'on essaierait de deviner, pas les identifiants du code :
 * on tape `aquarium` et non `aqua`, `glougle` et non `search`. Plusieurs mots
 * peuvent mener au même endroit — c'est fait pour être trouvé du premier coup.
 *
 * Un mot ne doit jamais **en masquer un autre** : la reconnaissance se fait sur
 * la fin du tampon de frappe, donc un mot dont un préfixe se termine par un
 * autre mot ne serait jamais atteint. `poissonpedia` en est l'exemple parfait —
 * `poisson` aurait déclenché à la septième lettre, et la suite serait tombée
 * dans le vide. D'où `pedia`. Un test vérifie qu'aucun mot n'en masque un autre.
 */
const SKIN_WORDS: readonly (readonly [word: string, skin: Skin])[] = [
  ['aquarium', 'aqua'],
  ['1998', 'y2k'],
  ['saas', 'saas'],
  ['paint', 'paint'],
  ['8bit', 'pixel'],
  ['matrix', 'matrix'],
  ['windowsxp', 'xp'],
  ['skyblog', 'skyblog'],
  ['3d', 'threed'],
  ['pedia', 'wiki'],
  ['glougle', 'search'],
];

/**
 * Tout ce qui se tape, dans l'ordre. Le premier qui correspond gagne, mais
 * l'ordre ne devrait jamais départager quoi que ce soit : voir la règle de
 * masquage ci-dessus.
 */
export const SECRETS: readonly (readonly [word: string, action: SecretAction])[] = [
  ['steve', { kind: 'reroll' }],
  ['orange', { kind: 'egg' }],
  ['poisson', { kind: 'fish' }],
  ...SKIN_WORDS.map(
    ([word, skin]): readonly [string, SecretAction] => [word, { kind: 'skin', skin }],
  ),
];

/** Tampon de frappe : la longueur du plus long mot. */
export const SECRET_BUFFER = Math.max(...SECRETS.map(([word]) => word.length));

/** Les caractères qui peuvent entrer dans le tampon. */
export function isSecretKey(key: string): boolean {
  return key.length === 1;
}

/**
 * Ce que déclenche un tampon de frappe, ou `null` s'il ne déclenche rien.
 *
 * On regarde la **fin** du tampon : le visiteur n'a aucune raison de savoir
 * qu'il faut commencer proprement, et « steveaquarium » doit marcher.
 */
export function matchSecret(buffer: string): SecretAction | null {
  const flat = buffer.toLowerCase();
  for (const [word, action] of SECRETS) {
    if (flat.endsWith(word)) return action;
  }
  return null;
}

/**
 * Les mots qu'un autre mot rend inatteignables.
 *
 * Un mot `W` est masqué si l'un de ses préfixes stricts se termine par un autre
 * mot : ce mot-là déclencherait avant que `W` soit complet, et remettrait le
 * tampon à zéro. La fonction existe pour que le test puisse le vérifier sur la
 * table réelle plutôt que sur une liste écrite à la main, qui se périmerait au
 * premier ajout.
 */
export function shadowedWords(): readonly string[] {
  const words = SECRETS.map(([word]) => word);
  return words.filter((word) =>
    words.some(
      (other) =>
        other !== word &&
        /*
         * Un préfixe strict de `word` se termine-t-il par `other` ? C'est le
         * cas exactement quand `other` tient entier dans `word` privé de sa
         * dernière lettre — un `endsWith` ne verrait que le plus long préfixe
         * et laisserait passer « abcdef » masqué par « bc ».
         */
        word.slice(0, -1).includes(other),
    ),
  );
}

/**
 * Le mot qui mène à une interface, ou `null` s'il n'y en a pas.
 *
 * Sert à Glougle : la carte de résultat d'une interface affiche son mot à côté
 * du bouton qui fait la même chose, comme un raccourci clavier dans un menu.
 * Le mot n'y est donc écrit qu'une fois, ici — un second exemplaire recopié
 * dans la table d'indexation aurait fini par mentir.
 */
export function wordForSkin(skin: Skin): string | null {
  for (const [word, action] of SECRETS) {
    if (action.kind === 'skin' && action.skin === skin) return word;
  }
  return null;
}
